import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IconButton } from '@/components/primitives/IconButton';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: string;
}

export function Modal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 'max-w-xl',
}: ModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  return createPortal(
    <div className="fixed inset-0 bg-[#00000099] backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      {/* Backdrop detector */}
      <div className="absolute inset-0 z-0" onClick={onClose} />

      {/* Modal Box */}
      <div
        className={`relative z-10 bg-surface-container-lowest w-full ${width} rounded-2xl shadow-2xl overflow-hidden border border-outline-variant/20 flex flex-col max-h-[90vh]`}
      >
        <div className="p-6 border-b border-outline-variant/10 flex justify-between items-center bg-surface-bright">
          <div>
            <h2 className="text-xl font-black text-on-surface font-headline">{title}</h2>
            {subtitle && (
              <p className="text-xs text-on-surface-variant font-medium mt-1">{subtitle}</p>
            )}
          </div>
          <IconButton icon="close" size="md" onClick={onClose} variant="ghost" />
        </div>

        <div className="overflow-y-auto p-8 flex-grow bg-surface-container-lowest">{children}</div>

        {footer && (
          <div className="p-6 border-t border-outline-variant/10 bg-surface-container-low/50">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
